import React from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';
import TagChip from '../../../atoms/TagChip';
import ArticleModalFooterActions from './ArticleModalFooterActions';
import ArticleModalMobileFooterActions from './ArticleModalMobileFooterActions';

const ArticleModalFooterBar = ({
  article,
  highlightEnabled,
  isPaused,
  isSearchOpen,
  isSpeaking,
  isTTSSupported,
  kpiaAnalyzing,
  qualitativeAnalysisApplied,
  onAddToCollection,
  onAnalyzeKpia,
  onDelete,
  onEdit,
  onListen,
  onOpenExposureMenu,
  onOpenMore,
  onOpenPublicLink,
  onOpenSearch,
  onOpenShare,
  onOpenTagDialog,
  onOpenSentimentMenu,
  onRemoveTag,
  onToggleFavorite,
  onToggleHighlight,
  onToggleRead,
  readOnly
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tagChipSx = {
    height: 20,
    minHeight: 20,
    px: 0.85,
    borderRadius: '999px',
    backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.72)' : '#64748B',
    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.72)' : '#64748B',
    color: isDarkMode ? '#111827' : '#FFFFFF',
    border: 0,
    fontSize: '0.62rem',
    fontWeight: 760,
    '& .MuiChip-label': {
      px: 0,
      color: isDarkMode ? '#111827' : '#FFFFFF',
      fontSize: '0.62rem',
      fontWeight: 760
    },
    '& .MuiChip-deleteIcon': {
      ml: 0.4,
      mr: -0.35,
      fontSize: '0.68rem',
      color: `${isDarkMode ? '#111827' : '#FFFFFF'} !important`
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'sticky',
        bottom: 0,
        zIndex: 1401,
        bgcolor: 'background.paper',
        borderTop: { xs: 'none', md: '1px solid' },
        borderColor: { md: 'divider' },
        boxShadow: { xs: '0 -6px 20px rgba(0,0,0,0.16)', md: 'none' },
        px: { xs: 0.5, md: 3 },
        pt: { xs: 0.75, md: 1.5 },
        pb: { xs: 'calc(0.875rem + env(safe-area-inset-bottom, 0px))', md: 1.5 },
        minHeight: { xs: 'calc(68px + env(safe-area-inset-bottom, 0px))', md: 'auto' },
        justifyContent: { xs: 'space-between', md: 'flex-end' },
        alignItems: 'center',
        gap: { xs: 0, md: 1 },
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <ArticleModalMobileFooterActions
        article={article}
        onToggleFavorite={onToggleFavorite}
        onToggleRead={onToggleRead}
        onOpenTagDialog={onOpenTagDialog}
        onOpenShare={onOpenShare}
        onOpenMore={onOpenMore}
        readOnly={readOnly}
      />

      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0, ml: { md: 0 }, gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', overflowX: 'auto', minWidth: 0, flex: '1 1 auto', pb: 0.5, '&::-webkit-scrollbar': { height: '4px' } }}>
          {article.tags && article.tags.length > 0 && article.tags.map((tag) => (
            <TagChip
              key={typeof tag === 'object' ? tag._id : tag}
              label={typeof tag === 'object' ? tag.name : tag}
              onDelete={onRemoveTag ? () => onRemoveTag(tag) : undefined}
              sx={tagChipSx}
            />
          ))}
        </Box>

        <Box sx={{ flex: '0 0 auto', maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden', pr: 0.5, '&::-webkit-scrollbar': { height: '4px' } }}>
          <ArticleModalFooterActions
            article={article}
            highlightEnabled={highlightEnabled}
            isPaused={isPaused}
            isSearchOpen={isSearchOpen}
            isSpeaking={isSpeaking}
            isTTSSupported={isTTSSupported}
            kpiaAnalyzing={kpiaAnalyzing}
            qualitativeAnalysisApplied={qualitativeAnalysisApplied}
            onAddToCollection={onAddToCollection}
            onAnalyzeKpia={onAnalyzeKpia}
            onDelete={onDelete}
            onEdit={onEdit}
            onListen={onListen}
            onOpenExposureMenu={onOpenExposureMenu}
            onOpenPublicLink={onOpenPublicLink}
            onOpenSearch={onOpenSearch}
            onOpenShare={onOpenShare}
            onOpenTagDialog={onOpenTagDialog}
            onOpenSentimentMenu={onOpenSentimentMenu}
            onToggleFavorite={onToggleFavorite}
            onToggleHighlight={onToggleHighlight}
            onToggleRead={onToggleRead}
            readOnly={readOnly}
          />
        </Box>
      </Box>
    </Box>
  );
};

ArticleModalFooterBar.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string,
    tags: PropTypes.array
  }).isRequired,
  highlightEnabled: PropTypes.bool.isRequired,
  isPaused: PropTypes.bool.isRequired,
  isSearchOpen: PropTypes.bool.isRequired,
  isSpeaking: PropTypes.bool.isRequired,
  isTTSSupported: PropTypes.bool.isRequired,
  kpiaAnalyzing: PropTypes.bool.isRequired,
  onAddToCollection: PropTypes.func.isRequired,
  onAnalyzeKpia: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onListen: PropTypes.func.isRequired,
  onOpenExposureMenu: PropTypes.func.isRequired,
  onOpenMore: PropTypes.func.isRequired,
  onOpenPublicLink: PropTypes.func.isRequired,
  onOpenSearch: PropTypes.func.isRequired,
  onOpenSentimentMenu: PropTypes.func.isRequired,
  onOpenShare: PropTypes.func.isRequired,
  onOpenTagDialog: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func,
  onToggleFavorite: PropTypes.func.isRequired,
  onToggleHighlight: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  qualitativeAnalysisApplied: PropTypes.bool.isRequired
};

ArticleModalFooterBar.defaultProps = {
  onDelete: null,
  onEdit: null,
  onRemoveTag: null,
  readOnly: false
};

export default ArticleModalFooterBar;
